import { supabase } from '@/integrations/supabase/client';
import { EventPassEntry } from '@/types';
import { riderKey } from '@/utils/cart';

export interface PassClass extends EventPassEntry {
  photos: number;
  swissRaceId: string;
}

export interface PassRider {
  key: string;
  riderName: string;
  swissRiderId: string;
  classes: PassClass[];
  totalPhotos: number;
}

/**
 * All riders of an event with their pass-eligible classes (same rules as
 * checkout: no GROUPED races, participant must have photos).
 */
export async function fetchEventPassRiders(eventUuid: string): Promise<PassRider[]> {
  const rows: any[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from('participants')
      .select('id, rider_name, horse_name, swiss_equestrian_rider_id, total_photos, photo_to, races!inner(id, event_id, race_type, category, swiss_equestrian_race_id)')
      .eq('races.event_id', eventUuid)
      .range(from, from + 999);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  // Fallback when total_photos was never saved: derive from the photo range.
  const derived = new Map<string, number>();
  const byRace = new Map<string, any[]>();
  for (const p of rows) if (p.photo_to != null) byRace.set(p.races?.id, [...(byRace.get(p.races?.id) || []), p]);
  for (const list of byRace.values()) {
    list.sort((a, b) => a.photo_to - b.photo_to);
    list.forEach((p, i) => { if (i > 0) derived.set(p.id, p.photo_to - list[i - 1].photo_to); });
  }
  const map = new Map<string, PassRider>();
  for (const p of rows) {
    const race = p.races;
    if (!race || race.race_type === 'GROUPED' || !race.swiss_equestrian_race_id) continue;
    const photos = p.total_photos || derived.get(p.id) || 0;
    if (photos <= 0) continue;
    const key = riderKey(p.swiss_equestrian_rider_id, p.rider_name);
    const r = map.get(key) || { key, riderName: p.rider_name, swissRiderId: p.swiss_equestrian_rider_id || '', classes: [], totalPhotos: 0 };
    r.classes.push({
      participantId: p.id,
      raceId: race.swiss_equestrian_race_id,
      swissRaceId: race.swiss_equestrian_race_id,
      raceCategory: race.category,
      horseName: p.horse_name,
      photos,
    });
    r.totalPhotos += photos;
    map.set(key, r);
  }
  return [...map.values()].sort((a, b) => a.riderName.localeCompare(b.riderName));
}

export const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export interface PassPhotoClass {
  participantId: string;
  raceCategory: string;
  horseName: string;
  urls: string[];
}

/** Watermarked preview URLs for each class of a pass, via the public search-participant-photos function. */
export async function fetchPassPhotos(
  entries: { participantId: string; raceCategory: string; horseName: string }[],
  eventSwissId: string,
): Promise<PassPhotoClass[]> {
  const ids = entries.map(e => e.participantId);
  const { data: rows } = await supabase.from('participants').select('id, race_id').in('id', ids);
  const raceOf = new Map((rows || []).map((r: any) => [r.id, r.race_id]));
  return Promise.all(entries.map(async e => {
    try {
      const { data } = await supabase.functions.invoke('search-participant-photos', {
        body: { participantId: e.participantId, raceId: raceOf.get(e.participantId), eventId: eventSwissId },
      });
      return { ...e, urls: (data?.photo_range?.urls || []) as string[] };
    } catch {
      return { ...e, urls: [] };
    }
  }));
}

/** Spread picks across classes so every class is represented. */
export function mixPreviews(classes: PassPhotoClass[], count = 6): string[] {
  const out: string[] = [];
  for (let round = 0; out.length < count; round++) {
    let added = false;
    for (const c of classes) {
      const step = Math.max(1, Math.floor(c.urls.length / 3));
      const u = c.urls[round * step];
      if (u && out.length < count) { out.push(u); added = true; }
    }
    if (!added) break;
  }
  return out;
}

/** Number of non-grouped classes of an event whose photos aren't online yet. */
export async function fetchPendingClasses(eventUuid: string): Promise<number> {
  const { count } = await supabase
    .from('races')
    .select('id', { count: 'exact', head: true })
    .eq('event_id', eventUuid)
    .neq('status', 'completed')
    .or('race_type.is.null,race_type.neq.GROUPED');
  return count || 0;
}
